import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import {
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from 'reactstrap';
import { getServices as onGetServices, getIncidentById, updateIncident,getComponents } from "../../store/actions";
import SideBar from "../../components/HorizontalLayout/Sidebar";
import Multiselect from "multiselect-react-dropdown";

const UpdateIncident = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { incident } = useSelector(state => ({
    incident: state.Incidents.incident
  }));

  const { services } = useSelector(state => ({
    services: state.Service.services
  }));

  const {  components } = useSelector(state => ({
    components: state.Incidents.components || []
  }));

  const getDefaultStatusMessage = (status) => {
    switch (status) {
      case "Investigating":
        return "We are currently investigating this issue.";
      case "Identified":
        return "We have identified the issue and are working on a fix.";
      case "Monitoring":
        return "The issue has been resolved and we are currently monitoring the situation.";
      case "Resolved":
        return "The issue has been resolved.";
      default:
        return "";
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    status: '',
    severity: '',
    description: '',
    message: '',
    sendNotifications: false
  });

  const [selectedComponents, setSelectedComponents] = useState([]);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    dispatch(getIncidentById(id));
    dispatch(getComponents());
  }, [dispatch, id]);

  const addComponentService = (selectedList,selectedItem) => {
    setSelectedComponents(selectedList);
  }

  const removeComponentService = (selectedList,removedItem) => {
    setSelectedComponents(selectedList);
  }

  useEffect(() => {
    if (incident) {
      setFormData({
        name: incident.name,
        status: { value: incident.status, label: incident.status },
        severity: { value: incident.severity, label: incident.severity },
        description: incident.description,
        message: getDefaultStatusMessage(incident.status),
        sendNotifications: false
      });

      const currentComponents = incident.affected_services.map(service => ({
        value: service.component_service_id,
        label: service.component_name || service.application_service_name
      }));
      setSelectedComponents(currentComponents);
    }
  }, [incident]);

  const componentOptions = components.map(component => ({
    value: component.id,
    label: component.name || component.application_service_name
  }));

  const incidentStatuses = [
    { value: 'Investigating', label: 'Investigating' },
    { value: 'Identified', label: 'Identified' },
    { value: 'Monitoring', label: 'Monitoring' },
    { value: 'Resolved', label: 'Resolved' }
  ];

  const severityLevels = [
    { value: 'Critical', label: 'Critical' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' }
  ];

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Investigating":
        return "badge-soft-warning";
      case "Identified":
        return "badge-soft-info";
      case "Monitoring":
        return "badge-soft-primary";
      case "Resolved":
        return "badge-soft-success";
      default:
        return "badge-soft-light";
    }
  };

  const customStyles = {
    container: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
      borderColor: '#485EC4',
      color: '#fff',
      minHeight: '38px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#485EC4',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#fff',
      ':hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        color: '#fff',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#fff' : '#fff',
      backgroundColor: state.isSelected ? '#485EC4' : 'transparent',
      '&:hover': {
        backgroundColor: '#485EC4',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#222736',
      maxHeight: '200px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '200px',
    }),
  };


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const shouldShowValidation = (field) => {
    return touched[field] && !formData[field];
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleSubmit = () => {
    setTouched({
      name: true,
      status: true,
      severity: true,
      description: true,
      message: true
    });

    if (!formData.name || !formData.status || !formData.severity || 
        !formData.description || !formData.message) {
      return;
    }
    const updatePayload = {
      id: id,
      name: formData.name,
      status: formData.status.value,
      severity: formData.severity.value,
      description: formData.description,
      message: formData.message,
      affected_service_details: selectedComponents.map(component => component.value)
    };
    dispatch(updateIncident(updatePayload));
    navigate('/status-page-management');
  };

  if (!incident) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <SideBar current_page={"Incident Management"}/>
      <div className="container-fluid p-4" style={{width: '70%', marginLeft: '20%', marginBottom: '50px'}}>
        <h4 className="text-white mb-4">Update Incident</h4>
        <Card className="bg-transparent">
          <CardBody>
            <Form>
              <div className="row mb-4">
                <div className="col-md-9">
                  <FormGroup className="mb-4">
                    <Label className="text-white">Title <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      onBlur={() => handleBlur('name')}
                      invalid={shouldShowValidation('name')}
                      className="form-control"
                    />
                    <FormFeedback>Please enter an incident name</FormFeedback>
                  </FormGroup>
                </div>
                <div className="col-md-3">
                  <FormGroup>
                    <Label className="text-white">Incident Severity <span className="text-danger">*</span></Label>
                    <Select
                      value={formData.severity}
                      onChange={(option) => handleInputChange('severity', option)}
                      options={severityLevels}
                      styles={customStyles}
                      onBlur={() => handleBlur('severity')}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                          ...theme.colors,
                          neutral0: '#222736',
                          primary: '#485EC4',
                        },
                      })}
                    />
                    {touched.severity && !formData.severity && (
                      <div className="text-danger small mt-1">Please select a severity level</div>
                    )}
                  </FormGroup>
                </div>
              </div>
              <FormGroup className="mb-4">
                <Label className="text-white">Affected Components</Label>
                <Multiselect
                  selectedValues={selectedComponents}
                  closeMenuOnSelect={true}
                  onSelect={addComponentService}
                  onRemove={removeComponentService}
                  displayValue="label"
                  options={componentOptions}
                  placeholder="Select Components"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                    colors: {
                      ...theme.colors,
                      neutral0: '#222736',
                      neutral5: '#485EC4',
                      neutral10: '#485EC4',
                      neutral60: '#ffffff',
                      neutral70: '#ffffff',
                      neutral80: '#ffffff',
                      neutral90: '#ffffff',
                      primary: '#485EC4',
                    },
                  })}
                />
              </FormGroup>
              <FormGroup className="mb-4">
                <Label className="text-white">Incident Description <span className="text-danger">*</span></Label>
                <Input
                  type="textarea"
                  rows="4"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  onBlur={() => handleBlur('description')}
                  invalid={shouldShowValidation('description')}
                  className="form-control"
                />
                <FormFeedback>Please enter a description</FormFeedback>
              </FormGroup>
              <FormGroup className="mb-4">
                <Label className="text-white">Published updates</Label>
                <div className="timeline-container border-start ps-3 ms-2">
                  {incident?.history?.length > 0 ? (
                    incident.history.map((event, index) => (
                      <div key={index} className="timeline-item mb-3">
                        <div className="d-flex flex-column">
                          {formatDate(event.timestamp)}
                          <div className="d-flex align-items-center gap-2 mt-1">
                                <span className={`badge ${getStatusBadgeColor(event.status)} px-2 py-1`}>
                                {event.status}
                                </span>
                            <span className="text-white">{event.message}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-white">No history available</div>
                  )}
                </div>
              </FormGroup>
              <div className="row mb-4 align-items-center">
                <div className="col-md-3">
                  <FormGroup>
                    <Label className="text-white">Status <span className="text-danger">*</span></Label>
                    <Select
                      value={formData.status}
                      onChange={(option) => {
                        handleInputChange('status', option);
                        handleInputChange('message', getDefaultStatusMessage(option.value));
                      }}
                      options={incidentStatuses}
                      styles={customStyles}
                      onBlur={() => handleBlur('status')}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                          ...theme.colors,
                          neutral0: '#222736',
                          primary: '#485EC4',
                        },
                      })}
                    />
                    {touched.status && !formData.status && (
                      <div className="text-danger small mt-1">Please select a status</div>
                    )}
                  </FormGroup>
                </div>
                <div className="col-md-9">
                  <FormGroup className="mb-1">
                    <Label className="text-white">Update Message <span className="text-danger">*</span></Label>
                    <textarea
                        className="w-100 input text-white"
                        style={{
                          minHeight: "39px",
                          height: "39px",
                          paddingTop: "8px",
                          paddingBottom: "0px",
                          boxSizing: "border-box",
                          overflow: "hidden",
                        }}
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        onBlur={() => handleBlur('message')}
                    />
                    {touched.message && !formData.message && (
                        <div className="text-danger small mt-1">Please enter an update message</div>
                    )}
                  </FormGroup>
                </div>
              </div>
              {/* <FormGroup className="mb-4">
              <div className="form-check">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  checked={formData.sendNotifications}
                  onChange={(e) => handleInputChange('sendNotifications', e.target.checked)}
                />
                <Label className="form-check-label text-white">Send Notifications</Label>
              </div>
            </FormGroup> */}
              <div className="d-flex justify-content-end gap-3 mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                >
                  Publish Update
                </button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default UpdateIncident;